import { useEffect, useMemo, useState } from "react";
import { Lock, X, ShieldAlert } from "lucide-react";
import Input from "./common/Input";
import Select from "./common/Select";
import { getSavedTopologies } from "../api/services/topologyService";

function normalizeSavedTopologiesList(savedRes) {
  if (Array.isArray(savedRes?.saved_topologies))
    return savedRes.saved_topologies;
  if (Array.isArray(savedRes?.topologies)) return savedRes.topologies;
  if (Array.isArray(savedRes)) return savedRes;
  return [];
}

const DIFFICULTY_OPTIONS = [
  { value: "", label: "Select difficulty" },
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
  { value: "legendary", label: "Legendary" },
];

const DURATION_OPTIONS = [
  { value: "", label: "Select duration" },
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "120", label: "2 hours" },
  { value: "240", label: "4 hours" },
];

const ATTACK_CHAIN_OPTIONS = [
  { value: "", label: "Select attack chain" },
  { value: "multi", label: "Multi-stage attack chain" },
  { value: "single", label: "One and done" },
];

const AI_COMPANION_OPTIONS = [
  { value: "", label: "Select AI companion mode" },
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
];

const CONTROL_OPTIONS = ["SIEM", "EDR", "Firewall", "IDS", "WAF"];

const NOISE_OPTIONS = [
  { value: "", label: "Select noise mode" },
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
];

const PERSPECTIVE_OPTIONS = [
  { value: "", label: "Select perspective" },
  { value: "blue", label: "Blue Team" },
  { value: "red", label: "Red Team" },
  { value: "purple", label: "Purple Team" },
];

function LockedFieldOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 flex items-start justify-end pr-3 pt-1">
      <div className="flex items-center gap-1 rounded-sm border border-(--color-border) bg-(--color-bg-card)/90 px-2 py-1 text-[10px] font-medium text-(--color-text-muted) backdrop-blur-sm">
        <Lock className="h-3 w-3" />
      </div>
    </div>
  );
}

export default function RequestScenarioModal({
  open,
  onClose,
  onSubmit,
  labs = [],
  prefilledLab = null,
  mode = "description", // "topology" | "description"
}) {
  const [selectedLab, setSelectedLab] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedTopologyId, setSelectedTopologyId] = useState("");
  const [savedTopologies, setSavedTopologies] = useState([]);
  const [isLoadingTopologies, setIsLoadingTopologies] = useState(false);
  const [topologyLoadError, setTopologyLoadError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    if (open) document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setSelectedLab("");
      setNotes("");
      setSelectedTopologyId("");
      setSavedTopologies([]);
      setTopologyLoadError("");
      setSubmitError("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || mode !== "topology") return;
    let isActive = true;
    const loadSavedTopologies = async () => {
      try {
        setIsLoadingTopologies(true);
        setTopologyLoadError("");
        const response = await getSavedTopologies();
        if (!isActive) return;
        setSavedTopologies(normalizeSavedTopologiesList(response));
      } catch (error) {
        if (!isActive) return;
        setSavedTopologies([]);
        setTopologyLoadError(
          error?.message || "Failed to load saved topologies.",
        );
      } finally {
        if (isActive) setIsLoadingTopologies(false);
      }
    };
    loadSavedTopologies();
    return () => {
      isActive = false;
    };
  }, [open, mode]);

  const options = useMemo(
    () =>
      labs.map((lab) => ({
        value: lab.id,
        label: lab.title || "Untitled lab",
      })),
    [labs],
  );

  const topologyOptions = useMemo(() => {
    const base = [{ value: "", label: "Select a saved topology" }];
    const mapped = savedTopologies.map((item, index) => ({
      value: String(item.id ?? item.topology_id ?? item.thread_id ?? index),
      label: item.name ?? item.title ?? `Saved Topology ${index + 1}`,
    }));
    return [...base, ...mapped];
  }, [savedTopologies]);

  if (!open) return null;

  const isSubmitDisabled = () => {
    if (isSubmitting) return true;
    if (!prefilledLab && !selectedLab) return true;
    if (mode === "description") return !notes.trim();
    if (mode === "topology") return !selectedTopologyId;
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitDisabled()) return;

    setIsSubmitting(true);
    setSubmitError("");

    let labData;
    if (prefilledLab) {
      labData = {
        labId: prefilledLab.id,
        labTitle: prefilledLab.title || "Unknown lab",
        labCve: prefilledLab.cve || "",
        categoryId: prefilledLab.category_id || "",
      };
    } else {
      const lab = labs.find((l) => l.id === selectedLab);
      labData = {
        labId: selectedLab,
        labTitle: lab?.title || "Unknown lab",
        labCve: lab?.cve || "",
        categoryId: lab?.category_id || "",
      };
    }

    const selectedTopology =
      mode === "topology"
        ? savedTopologies.find((item, index) => {
            const itemId = String(
              item.id ?? item.topology_id ?? item.thread_id ?? index,
            );
            return itemId === selectedTopologyId;
          })
        : null;

    const topologyId = selectedTopology
      ? String(
          selectedTopology.id ??
            selectedTopology.topology_id ??
            selectedTopology.thread_id ??
            selectedTopologyId,
        )
      : selectedTopologyId;

    try {
      if (onSubmit) {
        await onSubmit({
          ...labData,
          notes: notes.trim(),
          mode,
          ...(topologyId ? { topology_id: topologyId } : {}),
        });
      }
      onClose();
    } catch (error) {
      setSubmitError(error?.message || "Failed to submit request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalSubtitle =
    mode === "topology"
      ? "Select a saved topology to base your box on."
      : "Describe the vulnerability you want covered.";

  return (
    <div
      className="fixed inset-0 z-1000 flex items-center justify-center bg-black/70 backdrop-blur-xl px-4 py-8"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-180 flex-col rounded-sm border border-(--color-border) bg-(--color-bg-card) shadow-[0_24px_60px_rgba(0,0,0,0.18)]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-(--color-border) px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-(--color-primary)/20 bg-(--color-primary)/10">
              <ShieldAlert className="h-5 w-5 text-(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-(--color-text-primary)">
                Request Box Revision
              </h3>
              <p className="text-xs text-(--color-text-muted)">
                {modalSubtitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center text-(--color-text-muted) transition-colors hover:text-(--color-primary)"
            aria-label="Close"
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form
          className="flex-1 space-y-4 overflow-y-auto px-6 py-6"
          onSubmit={handleSubmit}
        >
          {/* Lab field */}
          {prefilledLab ? (
            <div>
              <label className="block text-sm font-medium mb-2 text-(--color-text-secondary)">
                Lab
              </label>
              <div className="w-full rounded-sm border border-(--color-border) bg-(--color-bg-muted) px-3 py-2.5 text-sm text-(--color-text-primary)">
                {prefilledLab.title || "Untitled lab"}
              </div>
            </div>
          ) : (
            <Select
              label="Lab"
              name="lab"
              value={selectedLab}
              onChange={(event) => setSelectedLab(event.target.value)}
              options={options}
              placeholder={
                options.length ? "Select a lab" : "No labs available"
              }
              required
            />
          )}

          {/* Topology dropdown — only in topology mode */}
          {mode === "topology" && (
            <>
              <Select
                label="Saved Topology"
                name="saved_topology"
                value={selectedTopologyId}
                onChange={(event) => setSelectedTopologyId(event.target.value)}
                options={topologyOptions}
                placeholder={
                  isLoadingTopologies
                    ? "Loading saved topologies..."
                    : "Select a topology"
                }
                required
              />
              {topologyLoadError && (
                <p className="-mt-2 text-xs text-red-400">
                  {topologyLoadError}
                </p>
              )}
            </>
          )}

          {/* Notes textarea — only in description mode */}
          {mode === "description" && (
            <div>
              <label className="block text-sm font-medium mb-2 text-(--color-text-secondary)">
                Request details
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                rows={4}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Share the vulnerability you want added, key objectives, or constraints."
                required
                className="w-full rounded-sm border border-(--color-border) bg-(--color-bg) px-3 py-2.5 text-sm text-(--color-text-primary) placeholder-(--color-text-muted) focus:border-(--color-primary) focus:outline-none focus:ring-2 focus:ring-(--color-primary)/30"
              />
            </div>
          )}

          {/* Locked / coming-soon fields — not submitted to backend */}
          <div className="pt-2">
            <div className="mb-3 flex items-center gap-2 border-t border-dashed border-(--color-border) pt-4">
              <Lock className="h-3.5 w-3.5 text-(--color-text-muted)" />
              <p className="text-xs font-medium uppercase tracking-wider text-(--color-text-muted)">
                Advanced options
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 opacity-60 md:grid-cols-2">
              <div className="relative">
                <Select
                  label="Difficulty Level"
                  name="difficulty"
                  value=""
                  onChange={() => {}}
                  options={DIFFICULTY_OPTIONS}
                  placeholder="Select difficulty"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative">
                <Select
                  label="Estimated Duration"
                  name="duration"
                  value=""
                  onChange={() => {}}
                  options={DURATION_OPTIONS}
                  placeholder="Select duration"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative">
                <Input
                  label="Specific MITRE TTPs"
                  name="ttps"
                  value=""
                  onChange={() => {}}
                  placeholder="e.g. T1059, T1110, T1486"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative">
                <Select
                  label="Attack Chain"
                  name="attackChain"
                  value=""
                  onChange={() => {}}
                  options={ATTACK_CHAIN_OPTIONS}
                  placeholder="Select attack chain"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative md:col-span-2">
                <Select
                  label="AI Companion"
                  name="aiCompanion"
                  value=""
                  onChange={() => {}}
                  options={AI_COMPANION_OPTIONS}
                  placeholder="Select AI companion mode"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-(--color-text-secondary)">
                  Specific Controls
                </label>
                <div className="flex flex-wrap gap-2 rounded-xl border-2 border-(--color-border) bg-(--color-bg-card) px-4 py-3">
                  {CONTROL_OPTIONS.map((control) => (
                    <label
                      key={control}
                      className="flex cursor-not-allowed items-center gap-2 rounded-sm border border-(--color-border) px-3 py-1.5 text-xs text-(--color-text-muted)"
                    >
                      <input
                        type="checkbox"
                        disabled
                        className="h-3 w-3 cursor-not-allowed"
                      />
                      {control}
                    </label>
                  ))}
                </div>
                <LockedFieldOverlay />
              </div>

              <div className="relative">
                <Select
                  label="Legitimate User Noise"
                  name="userNoise"
                  value=""
                  onChange={() => {}}
                  options={NOISE_OPTIONS}
                  placeholder="Select noise mode"
                  disabled
                />
                <LockedFieldOverlay />
              </div>

              <div className="relative">
                <Select
                  label="Attack Perspective"
                  name="perspective"
                  value=""
                  onChange={() => {}}
                  options={PERSPECTIVE_OPTIONS}
                  placeholder="Select perspective"
                  disabled
                />
                <LockedFieldOverlay />
              </div>
            </div>
          </div>

          {submitError && <p className="text-sm text-red-400">{submitError}</p>}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-sm border border-(--color-border) text-sm text-(--color-text-muted) transition hover:border-(--color-primary)/40 hover:text-(--color-primary) disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled()}
              className="px-4 py-2 rounded-sm border border-(--color-primary) bg-(--color-primary) text-sm font-semibold text-(--color-text-on-primary) transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
